import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useJournalEntriesQuery } from '@/api/queries/journal';
import { useChartOfAccounts } from '@/api/queries';
import type {
    JournalEntry,
    JournalEntrySummary,
    LocaleCode,
} from '@/api/models';
import type {
    JournalListQuery,
    JournalSourceFilter,
    JournalStatusFilter,
} from '@/api/handlers/resources/journal/requests';
import { useBooksRange } from '../booksOutletContext';
import { defaultBooksWindow } from '../booksWindow';
import { JOURNAL_PAGE_SIZE } from './journalMeta';

const SUPPORTED_LOCALES: LocaleCode[] = ['ka', 'en'];
const resolveLocale = (language: string): LocaleCode =>
    (SUPPORTED_LOCALES as string[]).includes(language)
        ? (language as LocaleCode)
        : 'en';

type UseJournalPageParams = {
    /** Open the New-entry modal on mount (deep link `/accounting/journal/new`). */
    initialNewOpen?: boolean;
};

/** Modal target: `null` = create, an entry = edit that draft, `undefined` = closed. */
type ModalState = { open: boolean; entry: JournalEntry | null };

export const useJournalPage = ({
    initialNewOpen = false,
}: UseJournalPageParams = {}) => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const [searchParams, setSearchParams] = useSearchParams();

    // Chart-of-Accounts drill-in: `?account=<id>` scopes the list.
    const accountId = searchParams.get('account');
    // Audit-trail deep link: `?entry=<id>` opens one entry (the Periods screen
    // links here from a posted close / rollover / reversal).
    const entryParam = searchParams.get('entry');

    const [status, setStatus] = React.useState<JournalStatusFilter>('ALL');
    // Origin filter (BOS-119). Sales and purchases now post entries of their
    // own, so the journal is no longer a list of hand-keyed corrections and
    // needs a way to separate the day's trading from the accountant's work.
    const [source, setSource] = React.useState<JournalSourceFilter>('ALL');
    // The Books shell owns the window — this page has no range control of its
    // own, and unlike its `/accounting` counterpart it holds no range state.
    // A drill from a report already arrives with the shell's `?from`/`?to`
    // intact, so the list opens on the window the figure was read from.
    const {
        fromDate: shellFrom,
        toDate: shellTo,
        onRangeChange: onShellRangeChange,
    } = useBooksRange();
    // The shell owns the window; this only covers the standalone case where the
    // journal renders outside it. Same rule either way (BOS-166).
    const shellless = React.useMemo(() => defaultBooksWindow(dayjs()), []);
    const fromDate = shellFrom || shellless.from;
    const toDate = shellTo || shellless.to;

    /**
     * The window the empty state offers to widen to: the twelve months ending
     * today.
     *
     * A window with no entries is the one case where the picker is the answer
     * and nothing on screen says so — an accountant arriving on the 2nd used to
     * get "0 entries" and a dead end. Twelve months rather than a computed
     * "most recent month with entries": it needs no extra query, it cannot be
     * wrong, and one click is one click either way.
     */
    const widenWindow = React.useMemo(() => {
        const today = dayjs();
        return {
            from: today.subtract(12, 'month').format('YYYY-MM-DD'),
            to: today.format('YYYY-MM-DD'),
        };
    }, []);

    /**
     * `undefined` when there is nobody to widen *for* (BOS-171).
     *
     * `widenWindow` is always a window, so `model.widenWindow ? …` in the View
     * was always true and the button always rendered — including where it would
     * have done nothing, because `onShellRangeChange` is what actually moves the
     * shell. Journal is only mounted under `BooksPage` today, so that case is
     * unreachable; a button that promises an action it cannot take is still the
     * wrong thing to leave in the code.
     */
    const onWiden = React.useMemo(
        () =>
            onShellRangeChange === undefined
                ? undefined
                : () => onShellRangeChange(widenWindow.from, widenWindow.to),
        [onShellRangeChange, widenWindow],
    );
    const [page, setPage] = React.useState(1);

    // Any filter change returns to page 1.
    React.useEffect(() => {
        setPage(1);
    }, [status, source, fromDate, toDate, accountId]);

    const query: JournalListQuery = {
        page,
        pageSize: JOURNAL_PAGE_SIZE,
        status,
        source,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        accountId: accountId ?? undefined,
    };
    const listQuery = useJournalEntriesQuery(query);
    const listResponse = listQuery.data;
    const list = listResponse?.success ? listResponse.response : null;
    // Memoised because it feeds a `useMemo` below: a bare `?? []` mints a
    // fresh array on every render, so every dependent recomputes forever.
    const entries: JournalEntrySummary[] = React.useMemo(
        () => list?.items ?? [],
        [list],
    );
    const total = list?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / JOURNAL_PAGE_SIZE));
    const isLoading = listQuery.isLoading;
    const loadError =
        listResponse && !listResponse.success ? listResponse.error : null;

    // Modal (create / edit-draft) + drawer (detail) state.
    const [modal, setModal] = React.useState<ModalState>({
        open: initialNewOpen,
        entry: null,
    });
    const [selectedId, setSelectedId] = React.useState<string | null>(
        entryParam,
    );

    // Follow the deep link when it changes mid-session (a second close, say).
    //
    // Its `/accounting` counterpart also cleared the date bound here, so an
    // entry dated at a year end wasn't left behind a drawer over a list it isn't
    // in. That is not this page's call to make any more: the window belongs to
    // the shell, and a tab that silently widened it would leave the picker above
    // describing a list it no longer matches. The drawer still opens; the entry
    // may not be in the list behind it unless the shell's range covers its date.
    React.useEffect(() => {
        if (entryParam) {
            setSelectedId(entryParam);
        }
    }, [entryParam]);

    // Arriving via `?account=`: auto-open the drawer for the first match.
    const autoOpenedRef = React.useRef(false);
    React.useEffect(() => {
        if (!accountId) {
            autoOpenedRef.current = false;
            return;
        }
        if (!autoOpenedRef.current && entries.length > 0) {
            setSelectedId(entries[0]!.id);
            autoOpenedRef.current = true;
        }
    }, [accountId, entries]);

    // Resolve the filtered account's label for the filter chip.
    const chartQuery = useChartOfAccounts({ locale });
    const accounts = chartQuery.data;

    // Retry for the page's ErrorBoundary. Refetches *every* query the page
    // reads, not just the list — the account-filter label comes from the chart,
    // and a retry that leaves it stale would half-recover the page. The boundary
    // itself clears via `resetKeys` once `loadError` goes.
    const listRefetch = listQuery.refetch;
    const chartRefetch = chartQuery.refetch;
    const onRetry = React.useCallback(() => {
        void listRefetch();
        void chartRefetch();
    }, [listRefetch, chartRefetch]);
    const filterAccount = accountId
        ? (accounts?.find((account) => account.id === accountId) ?? null)
        : null;
    const filterAccountLabel = filterAccount
        ? `${filterAccount.code} ${filterAccount.name}`
        : accountId;

    const onClearAccountFilter = React.useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('account');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Handlers.
    const onStatusChange = React.useCallback(
        (next: JournalStatusFilter) => setStatus(next),
        [],
    );
    const onSourceChange = React.useCallback(
        (next: JournalSourceFilter) => setSource(next),
        [],
    );
    const onPageChange = React.useCallback((next: number) => setPage(next), []);

    const onNewEntry = React.useCallback(
        () => setModal({ open: true, entry: null }),
        [],
    );
    const onEditEntry = React.useCallback((entry: JournalEntry) => {
        setSelectedId(null);
        setModal({ open: true, entry });
    }, []);
    const onCloseModal = React.useCallback(
        () => setModal((prev) => ({ ...prev, open: false })),
        [],
    );
    const onSaved = React.useCallback(() => {
        // The mutation invalidates the list; just close the modal.
        setModal((prev) => ({ ...prev, open: false }));
    }, []);

    const onSelectEntry = React.useCallback(
        (id: string) => setSelectedId(id),
        [],
    );
    const onCloseDrawer = React.useCallback(() => setSelectedId(null), []);

    return {
        locale,
        // data
        entries,
        total,
        totalPages,
        page,
        isLoading,
        loadError,
        onRetry,
        // filters — the date window is the shell's, and read-only here
        fromDate,
        toDate,
        /**
         * The widen the empty state offers, and the callback that applies it.
         * `null` when the journal renders outside the Books shell, which owns
         * the window — an offer that cannot be honoured is worse than none.
         */
        widenWindow,
        onWiden,
        status,
        onStatusChange,
        source,
        onSourceChange,
        onPageChange,
        // account drill-in
        accountId,
        filterAccountLabel,
        onClearAccountFilter,
        // modal
        isModalOpen: modal.open,
        modalEntry: modal.entry,
        onNewEntry,
        onEditEntry,
        onCloseModal,
        onSaved,
        // drawer
        selectedId,
        onSelectEntry,
        onCloseDrawer,
    };
};

export type JournalPageModel = ReturnType<typeof useJournalPage>;
